import { uuid } from "@@common";
import { IVariableScope } from "../../Models/IVariableScope";
import { VariableScopeObserver } from "./VariableScopeObserver";

export class ObjectVariableScope implements IVariableScope {
  private readonly id: string = uuid();
  private obj: Record<string, any>;
  private parent: IVariableScope | null = null;
  private parentKey: string | null = null;
  private parentPath: string | null = null;

  private observers?: Set<VariableScopeObserver>;

  // Single static notification queue to ensure all notifications are processed in order without races, even if multiple changes happen in quick succession across different scopes
  private static notificationQueue: Promise<void> = Promise.resolve();

  constructor(obj: Record<string, any> = {}, parent: IVariableScope | null = null, parentKey: string | null = null) {
    this.obj = obj;
    this.parent = parent;
    this.parentKey = parentKey;

    const parentPath = (parent as ObjectVariableScope)?.parentPath || null;

    this.parentPath = parentPath !== null && parentKey !== null
      ? `${parentPath}.${parentKey}`
      : parentPath !== null
        ? parentPath
        : parentKey;
  }

  getId(): string {
    return this.id;
  }

  getParent(): IVariableScope | null {
    return this.parent;
  }

  addObserver(observer: VariableScopeObserver): void {
    if (!this.observers) {
      this.observers = new Set();
    }
    this.observers.add(observer);
  }

  removeObserver(observer: VariableScopeObserver): void {
    this.observers?.delete(observer);
  }

  getVariables(): string[] {
    // Return the variables in the current scope (not including parent scope)
    return Object.keys(this.obj);
  }

  notifyChange(name: string | null, value: any, prevValue: any): void {

    // Early return if no observers and no parent to notify
    if (!this.observers && (!this.parent || this.parentKey === null)) {
      return;
    }
    console.log(`[ObjectVariableScope] Scope ID: ${this.getId()} notifying change for '${name}'`, value, prevValue);

    // Build the full path (cached in parentPath, just append name if needed)
    const path = this.parentPath && name ? `${this.parentPath}.${name}` : (this.parentPath || name);

    // Queue the notification work to be executed asynchronously in order
    ObjectVariableScope.notificationQueue = ObjectVariableScope.notificationQueue.then(async () => {
      if (this.observers && this.observers.size > 0) {
        console.log(`[ObjectVariableScope] Notifying observers ${this.observers.size} of change to '${name}':`, value, prevValue);

        for (const observer of this.observers) {
          const result = observer(this, path, value, prevValue);
          // Check if observer returned a promise and await it
          if (result instanceof Promise) {
            await result;
          }
        }
      }

      // Notify the parent
      const parentObjectVariableScope = this.parent as ObjectVariableScope;
      if (parentObjectVariableScope?.notifyChange) {
        parentObjectVariableScope.notifyChange(path, this, this);
      }
    }).catch(error => {
      console.error(`[ObjectVariableScope] Error in notification queue:`, error);
    });
  }

  /**
   * Traverses through a path of property names, optionally creating intermediate objects
   * @param path Array of property names to traverse
   * @param isAssignment If true, creates intermediate objects when they don't exist
   * @param startFrom Starting object/scope (defaults to this.obj for assignment, this for reading)
   * @param length Number of elements to traverse (defaults to full path length)
   * @returns The final object reached after  traversal, or undefined if path doesn't exist
   */
  private traversePath(path: string[], isAssignment: boolean, startFrom?: any, length?: number): any | undefined {
    let current: any = startFrom ?? (isAssignment ? this.obj : this);
    let currentScope: ObjectVariableScope | null = startFrom === this.obj ? null : this;
    const pathLength = length ?? path.length;

    if (current == null) {
      return undefined;
    }

    for (let i = 0; i < pathLength; i++) {
      const varName = path[i];

      // If current is an IVariableScope, use tryGet
      if (typeof current.tryGet === "function") {
        let next = current.tryGet(varName);
        if (next == null) {
          if (isAssignment) {
            // Create a new ObjectVariableScope for this path element to maintain scope hierarchy
            next = new ObjectVariableScope({}, current, varName);
            current.set(varName, next);
          } else {
            // When reading, return undefined if path doesn't exist
            return undefined;
          }
        }
        currentScope = next as ObjectVariableScope;
        current = next;
      }
      // Otherwise, try to access as object property
      else if (typeof current === "object") {
        let next = current[varName];
        if (next == null) {
          if (isAssignment) {
            // Create a new ObjectVariableScope wrapped around a plain object to maintain notification chain
            const parentScope = currentScope || this;
            next = new ObjectVariableScope({}, parentScope, varName);
            current[varName] = next;
          } else {
            // When reading, return undefined if path doesn't exist
            return undefined;
          }
        }

        currentScope = typeof next?.tryGet === "function" ? (next as ObjectVariableScope) : null;
        current = next;
      } else {
        return undefined;
      }

      if (isAssignment && current == null) {
        throw new Error(`Cannot navigate through null/undefined at path '${path.slice(0, i + 1).join(".")}'`);
      }

      if (isAssignment && typeof current !== "object") {
        throw new Error(`Cannot navigate through non-object type at path '${path.slice(0, i + 1).join(".")}'`);
      }
    }

    return current;
  }

  set(name: string | string[], value: any): void {
    // Handle path array for nested property access
    if (Array.isArray(name)) {
      if (name.length === 0) {
        throw new Error("Cannot assign to empty variable path");
      }

      if (name.length === 1) {
        // Single element path, use simple set
        this.set(name[0], value);
        return;
      }

      // Traverse to the parent object/scope, creating intermediate objects as needed
      const lastIndex = name.length - 1;
      const current = this.traversePath(name, true, this.obj, lastIndex);

      const finalVarName = name[lastIndex];

      // If parent is an IVariableScope, use set (which will trigger notifyChange on that scope)
      if (typeof current.set === "function") {
        current.set(finalVarName, value);
        // No need to call notifyChange here - current.set will handle it and bubble up
      }
      // Otherwise, assign as object property and notify change on the root scope
      else if (typeof current === "object") {
        const prevValue = current[finalVarName];
        current[finalVarName] = value;

        // Notify from root scope - the path will be constructed in notifyChange
        this.notifyChange(null, value, prevValue);
      } else {
        throw new Error(`Cannot assign to non-object type`);
      }
      return;
    }

    // Handle simple string name
    // If the variable does not exist in the current scope but exists in the parent, set it in the parent
    if (!this.obj.hasOwnProperty(name) && this.parent && this.parent.tryGet(name) !== undefined) {
      this.parent.set(name, value);
      return;
    }

    // otherwise, set in the current scope
    const prevValue = this.obj[name];
    this.obj[name] = value;
    this.notifyChange(name, value, prevValue);
  }

  tryGet(name: string | string[]): any | undefined {
    // Handle path array for nested property access
    if (Array.isArray(name)) {
      if (name.length === 0) {
        return undefined;
      }

      if (name.length === 1) {
        // Single element path, use simple tryGet
        return this.tryGet(name[0]);
      }

      // Traverse through the path
      return this.traversePath(name, false, undefined, name.length);
    }

    // Handle simple string name
    // First check in the current scope
    if (this.obj.hasOwnProperty(name)) {
      return this.obj[name];
    }
    // Then check in the parent scope if available
    return this.parent?.tryGet(name);
  }
}
